import Vue from 'vue'
import { gaPage } from '../utils/analytics'
import Router from 'vue-router'
import Home from '../views/Home.vue'
import Login from '../views/Login'
import Profile from '../views/Profile/Profile'
import EditLinks from '../views/Profile/EditLinks'
import PrivacyPolicy from '../views/Legal/PrivacyPolicy'
import ContactUs from '../views/ContactUs'
import TermsAndConditions from '../views/Legal/TermsAndConditions'
import ForgotPassword from '../views/Password/ForgotPassword'
import ResetPassword from '../views/Password/ResetPassword'
import NotFound from '../views/NotFound'
import NotAuthorized from '../views/NotAuthorized'
import SignupWrapper from '../views/SignUp/SignupWrapper'
import CreateAccount from '../views/SignUp/CreateAccount'
import VerifyEmail from '../views/SignUp/VerifyEmail'
import ChooseHandle from '../views/SignUp/ChooseHandle'
import VerifyCheck from '../views/SignUp/VerifyCheck'
import {
    editProfileGuard,
    verifyEmailGuard,
    chooseHandleGuard
} from './guards'
import UserBus from '../utils/buses/UserBus'

Vue.use(Router)

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            // scroll to saved position if available
            return savedPosition
        } else {
            // scroll to top of page on new route navigation
            return { x: 0, y: 0 }
        }
    },
    routes: [
        {
            path: '/',
            name: 'home',
            component: Home,
            meta: {
                title: 'Come Connect',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Make it easy for your people to connect with you. Keep everything in one place.'
                    }
                ]
            }
        },
        {
            path: '/sign-up',
            name: 'signUp',
            redirect: { name: 'createAccount' },
            component: SignupWrapper,
            children: [
                {
                    path: 'create-account',
                    name: 'createAccount',
                    component: CreateAccount,
                    meta: {
                        title: 'Sign Up',
                        metaTags: [
                            {
                                name: 'description',
                                content: 'Sign up for Come Connect. Create an account and start sharing your handles.'
                            }
                        ]
                    }
                },
                {
                    path: 'verify-email',
                    name: 'verifyEmail',
                    component: VerifyEmail,
                    meta: {
                        title: 'Verify Email'
                    },
                    beforeEnter: verifyEmailGuard
                },
                {
                    path: 'choose-handle',
                    name: 'chooseHandle',
                    component: ChooseHandle,
                    meta: {
                        title: 'Choose Handle'
                    },
                    beforeEnter: chooseHandleGuard
                }
            ]
        },
        {
            path: '/sign-up/verify-email/check/:token',
            name: 'verifyCheck',
            component: VerifyCheck,
            meta: {
                title: 'Verify Email Check',
                metaTags: []
            }

        },
        {
            path: '/login',
            name: 'login',
            component: Login,
            meta: {
                title: 'Login',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Log into Come Connect. Start sharing your handles.'
                    }
                ]
            }
        },
        {
            path: '/contact-us',
            name: 'contactUs',
            component: ContactUs,
            meta: {
                title: 'Contact Us',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Have a question or need support? Contact Us today.'
                    }
                ]
            }
        },
        {
            path: '/privacy-policy',
            name: 'privacyPolicy',
            component: PrivacyPolicy,
            meta: {
                title: 'Privacy Policy',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Learn about Come Connect\'s privacy policy. '
                    }
                ]
            }
        },
        {
            path: '/terms-and-conditions',
            name: 'termsAndConditions',
            component: TermsAndConditions,
            meta: {
                title: 'Terms and Conditions',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Log about Come Connect\'s terms of service.'
                    }
                ]
            }
        },
        {
            path: '/forgot-password',
            name: 'forgotPassword',
            component: ForgotPassword,
            meta: {
                title: 'Forgot Password',
                metaTags: [
                    {
                        name: 'description',
                        content: 'Forgot your Come Connect password? Start the recovery process.'
                    }
                ]
            }
        },
        {
            path: '/reset-password',
            name: 'resetPassword',
            component: ResetPassword,
            meta: {
                title: 'Reset Password',
                metaTags: []
            }
        },
        // IMPORTANT: this must go after the other routes since it dynamically matches routes
        {
            path: '/:handle',
            name: 'profile',
            component: Profile,
            meta: {
                title: 'Profile',
                metaTags: []
            }
        },
        {
            path: '/:handle/edit/links',
            name: 'editLinks',
            component: EditLinks,
            meta: {
                title: 'Edit Links',
                metaTags: []
            },
            beforeEnter: editProfileGuard
        },
        {
            path: '/not-authorized',
            name: 'notAuthorized',
            component: NotAuthorized,
            meta: {
                title: 'Not Authorized'
            }
        },
        {
            path: '*',
            name: 'notFound',
            component: NotFound,
            meta: {
                title: 'Page Not Found',
                metaTags: []
            }
        }
    ]
})

router.beforeEach(async (to, from, next) => {
    // if first load, attempt to refresh token
    if (from.name === null) {
        try {
            await UserBus.refreshToken()
        } catch {
            // do nothing
        }
    }

    const VUE_ROUTER_DATA_ATTRIBUTE = 'data-vue-router-controlled'
    const {
        title,
        metaTags
    } = to.meta
    if (title) {
        document.title = title
    }

    // remove old meta tags
    Array.from(document.querySelectorAll(`[${VUE_ROUTER_DATA_ATTRIBUTE}]`)).map(el => el.parentNode.removeChild(el))

    if (metaTags) {
        // add new meta tags
        metaTags.map(item => {
            const tag = document.createElement('meta')

            Object.keys(item).forEach(key => {
                tag.setAttribute(key, item[key])
            })

            tag.setAttribute(VUE_ROUTER_DATA_ATTRIBUTE, '')

            return tag
        })
            .forEach(el => {
                document.head.appendChild(el)
            })
    }

    next()
})

router.afterEach((to, from, next) => {
    // record navigation in google analytics
    gaPage(to.path)
})

export default router
